import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Read-only "presentation mode" context.
 *
 * Trigger it by appending `?present=1` to any URL, e.g.
 *     https://propveiw.com/?present=1
 *     https://propveiw.com/dashboard?present=1
 *
 * When enabled we hide dev/beta chrome so the app looks like a shipped
 * product on-camera:
 *   - Beta banner
 *   - Cookie consent banner
 *   - "Demo Manager" / "View as customer" pills
 *   - AI-chat launcher (optional — keeps focus on core flows)
 *
 * Once turned on the flag persists for the tab in sessionStorage so the
 * recorder doesn't have to re-add the param on every navigation.
 */
const KEY = 'propveiw_present_mode';

const PresentContext = createContext({ presentMode: false });

export const PresentModeProvider = ({ children }) => {
  const [presentMode, setPresentMode] = useState(false);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const on = url.searchParams.get('present');
      if (on === '1' || on === 'true') {
        sessionStorage.setItem(KEY, '1');
        setPresentMode(true);
      } else if (on === '0' || on === 'false') {
        sessionStorage.removeItem(KEY);
        setPresentMode(false);
      } else if (sessionStorage.getItem(KEY) === '1') {
        setPresentMode(true);
      }
    } catch (err) {
      // sessionStorage / URL access can throw in unusual browser modes
      // (private browsing, sandboxed iframes). Present-mode is a UX
      // nicety, so we just skip it rather than break the whole page.
      console.debug('PresentMode init skipped:', err?.message);
    }
  }, []);

  return (
    <PresentContext.Provider value={{ presentMode }}>
      {children}
    </PresentContext.Provider>
  );
};

export const usePresentMode = () => useContext(PresentContext);
